import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../config";
import { useTranslation } from "react-i18next";
import { clearPreviewEditSession, readPreviewEditSession } from "../utils/previewMap";

/* =====================
   ICONS
===================== */

const blackIcon = L.divIcon({
  className: "care-map-marker-shell",
  html: `
    <div class="care-map-marker care-map-marker-elder" aria-label="Activated elder">
      <span>E</span>
    </div>
  `,
  iconSize: [46, 54],
  iconAnchor: [23, 50],
  popupAnchor: [0, -48]
});

const APPOINTMENT_ALERT_WINDOW_MS = 15 * 60 * 1000;
const APPOINTMENT_SOON_WINDOW_MS = 10 * 60 * 1000;
const ACTIVATED_MAP_REFRESH_MS = 30 * 1000;

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const getAppointmentTime = (appointment = {}) => {
  const value = appointment.scheduledForText || appointment.scheduledFor;
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
};

const getAppointmentAlertStatus = (appointment, now = Date.now()) => {
  const appointmentTime = getAppointmentTime(appointment);
  if (!appointmentTime) return "";

  const delta = appointmentTime - now;
  if (delta <= 0 && Math.abs(delta) <= APPOINTMENT_ALERT_WINDOW_MS) return "now";
  if (delta > 0 && delta <= APPOINTMENT_SOON_WINDOW_MS) return "soon";
  return "";
};

/* =====================
   COMPONENT
===================== */

const ActivatedMap = () => {
  const { caregiverID = "" } = useParams();
  const navigate = useNavigate();
  const workerID = caregiverID || localStorage.getItem("workerID");
  const token = localStorage.getItem("token");
  const caregiverSession = readPreviewEditSession();
  const caregiverSessionToken =
    caregiverSession?.caregiverID === workerID ? caregiverSession.token : "";
  const { t } = useTranslation(["map", "profile"]);

  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState(null);
  const [appointmentMessage, setAppointmentMessage] = useState("");
  const [urgentAppointmentID, setUrgentAppointmentID] = useState("");
  const [availability, setAvailability] = useState("available");
  const [availabilityUpdating, setAvailabilityUpdating] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [deviceTimeZone, setDeviceTimeZone] = useState(getBrowserTimeZone);
  const detailsSectionRef = useRef(null);
  const mapSectionRef = useRef(null);

  const fetchActivatedPatients = useCallback(() => {
    if (!workerID) return;

    const authHeaders = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(caregiverSessionToken ? { "x-preview-edit-session": caregiverSessionToken } : {})
    };
    const profileRequest = caregiverSessionToken
      ? axios.get(`${config.API_BASE_URL}/caregiver-temp-map/${workerID}`, {
          headers: authHeaders
        })
      : Promise.resolve(null);

    Promise.all([
      axios.get(`${config.API_BASE_URL}/worker/activated-patients`, {
        params: { workerID },
        headers: authHeaders
      }),
      axios.get(`${config.API_BASE_URL}/worker/appointments`, {
        params: { workerID },
        headers: authHeaders
      }),
      profileRequest
    ])
      .then(([patientsRes, appointmentsRes, profileRes]) => {
        const nextPatients = patientsRes.data.patients || [];
        setPatients(nextPatients);
        setAppointments(appointmentsRes.data.appointments || []);
        if (profileRes?.data?.caregiver) {
          setAvailability(
            profileRes.data.caregiver.status === "unavailable" ? "unavailable" : "available"
          );
        }
        setLastSyncedAt(new Date());
        setError(null);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setError(t("map:error.sessionExpired"));
        } else {
          setError(t("map:error.loadFailed"));
        }
      });
  }, [workerID, token, caregiverSessionToken, t]);

  const syncDeviceTimeZone = useCallback(() => {
    if (!workerID || (!token && !caregiverSessionToken)) return;
    const currentTimeZone = getBrowserTimeZone();
    if (!currentTimeZone) return;

    setDeviceTimeZone(currentTimeZone);
    void axios
      .patch(
        `${config.API_BASE_URL}/user/timezone`,
        {
          id: workerID,
          role: caregiverSessionToken ? "caregiver" : "worker",
          timeZone: currentTimeZone
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(caregiverSessionToken
              ? { "x-preview-edit-session": caregiverSessionToken }
              : {})
          }
        }
      )
      .then(fetchActivatedPatients)
      .catch(() => {});
  }, [caregiverSessionToken, fetchActivatedPatients, token, workerID]);

  useEffect(() => {
    const synchronizeWhenVisible = () => {
      if (document.visibilityState === "visible") syncDeviceTimeZone();
    };

    syncDeviceTimeZone();
    const interval = window.setInterval(syncDeviceTimeZone, 5 * 60 * 1000);
    window.addEventListener("focus", syncDeviceTimeZone);
    document.addEventListener("visibilitychange", synchronizeWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", syncDeviceTimeZone);
      document.removeEventListener("visibilitychange", synchronizeWhenVisible);
    };
  }, [syncDeviceTimeZone]);

  useEffect(() => {
    fetchActivatedPatients();

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        fetchActivatedPatients();
      }
    };
    const interval = window.setInterval(refreshWhenVisible, ACTIVATED_MAP_REFRESH_MS);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [fetchActivatedPatients]);

  const mapPoints = useMemo(
    () =>
      patients
        .filter((p) => p.latitude && p.longitude)
        .map((p) => [p.latitude, p.longitude]),
    [patients]
  );

  const upcomingAppointments = useMemo(
    () =>
      appointments.filter(
        (appointment) =>
          appointment.status !== "cancelled" &&
          (getAppointmentTime(appointment) || 0) >= Date.now() - APPOINTMENT_ALERT_WINDOW_MS
      ),
    [appointments]
  );

  useEffect(() => {
    const urgentAppointment = upcomingAppointments.find(
      (appointment) => getAppointmentAlertStatus(appointment) === "now"
    );

    setUrgentAppointmentID(urgentAppointment?.appointmentID || "");
  }, [upcomingAppointments]);

  const cancelAppointment = async (appointmentID) => {
    try {
      const response = await axios.patch(
        `${config.API_BASE_URL}/appointments/${appointmentID}/cancel`,
        { workerID },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(caregiverSessionToken ? { "x-preview-edit-session": caregiverSessionToken } : {})
          }
        }
      );
      setAppointmentMessage(
        response.data.message || "Appointment cancelled and email alerts were sent."
      );
      fetchActivatedPatients();
    } catch {
      setAppointmentMessage("Unable to cancel that appointment right now.");
    }
  };

  const updateAvailability = async (nextStatus) => {
    if (!caregiverSessionToken) {
      setAppointmentMessage("Unlock your caregiver profile again to change availability.");
      return;
    }

    try {
      setAvailabilityUpdating(true);
      const response = await axios.patch(
        `${config.API_BASE_URL}/caregiver-temp-map/${workerID}/availability`,
        { status: nextStatus },
        { headers: { "x-preview-edit-session": caregiverSessionToken } }
      );
      setAvailability(nextStatus);
      setAppointmentMessage(response.data.message);
      fetchActivatedPatients();
    } catch (availabilityError) {
      if (availabilityError.response?.status === 401) {
        clearPreviewEditSession(workerID);
      }
      setAppointmentMessage(
        availabilityError.response?.data?.error || "Unable to update availability."
      );
    } finally {
      setAvailabilityUpdating(false);
    }
  };

  return (
    <div style={page}>
      <style>{responsiveStyles}</style>

      <nav className="activated-mobile-nav" aria-label="Caregiver map shortcuts">
        <button
          type="button"
          onClick={() => detailsSectionRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Calls & alerts
        </button>
        <button
          type="button"
          onClick={() => mapSectionRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          View elders
        </button>
      </nav>

      <div style={layout} className="map-page-layout">
        <div ref={detailsSectionRef} style={legendPanel} className="map-side-panel">
          <button
            type="button"
            style={backToProfileButton}
            onClick={() =>
              navigate("/realMap", {
                state: {
                  focusCaregiverID: workerID,
                  successMessage: "Welcome back. Your caregiver profile is still signed in."
                }
              })
            }
          >
            ← Back to my profile
          </button>
          <h3 style={legendTitle}>{t("map:legend.activatedTitle")}</h3>

          <div style={availabilityCard}>
            <div>
              <div style={sectionEyebrow}>Your availability</div>
              <strong style={availabilityTitle}>
                {availability === "unavailable" ? "Unavailable" : "Available now"}
              </strong>
              <p style={availabilityCopy}>
                {availability === "unavailable"
                  ? "Elder calls and new appointments are paused."
                  : "Elders can call and schedule time with you."}
              </p>
            </div>
            <button
              type="button"
              style={availability === "unavailable" ? goAvailableButton : goUnavailableButton}
              disabled={availabilityUpdating}
              onClick={() =>
                updateAvailability(
                  availability === "unavailable" ? "available" : "unavailable"
                )
              }
            >
              {availabilityUpdating
                ? "Updating..."
                : availability === "unavailable"
                  ? "Go available"
                  : "Go unavailable"}
            </button>
          </div>

          <div style={legendCard}>
            <div style={legendItem}>
              <span style={legendDot}>E</span>

              <span style={legendText}>{t("map:legend.activatedMarker")}</span>
            </div>

            <p style={legendDescription}>
              This <b>black marker</b> represents an elder who has{" "}
              <span style={highlight}>activated you as their remote caregiver</span>.
            </p>

            <p style={legendDescription}>
              These elders already know you and trust you to support them. Open
              a marker to view details and contact them on WhatsApp.
            </p>

            {error && <div style={errorBox}>{error}</div>}
          </div>

          <div style={scheduleCard}>
            <div style={sectionEyebrow}>Calendar</div>
            <h4 style={scheduleTitle}>Elder-scheduled calls</h4>
            <p style={scheduleCopy}>
              Elders choose appointment times from their map after activating you.
              Keep this queue open so you can prepare and join WhatsApp calls on time.
            </p>

            <div style={alertHealthCard}>
              <span
                style={{
                  ...alertHealthDot,
                  background: "#16a34a"
                }}
              />
              <div style={alertHealthCopy}>
                <strong>Email alerts active</strong>
                <span>
                  Emails are sent when scheduled and ten minutes before the call{lastSyncedAt ? ` · synced ${lastSyncedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}. Times shown here use {deviceTimeZone || "this device's current timezone"}.
                </span>
              </div>
            </div>

            {appointmentMessage && <div style={messageBox}>{appointmentMessage}</div>}
          </div>

          <div style={appointmentsCard}>
            <div style={sectionEyebrow}>Upcoming</div>
            <h4 style={scheduleTitle}>Appointment queue</h4>
            {upcomingAppointments.length ? (
              <div style={appointmentList}>
                {upcomingAppointments.map((appointment) => (
                  <div
                    key={appointment.appointmentID}
                    style={{
                      ...appointmentItem,
                      ...(appointment.appointmentID === urgentAppointmentID ? urgentAppointmentItem : {}),
                      ...(getAppointmentAlertStatus(appointment) === "soon" ? soonAppointmentItem : {})
                    }}
                  >
                    {appointment.appointmentID === urgentAppointmentID && (
                      <span style={urgentBadge}>Time for appointment</span>
                    )}
                    {getAppointmentAlertStatus(appointment) === "soon" && (
                      <span style={soonBadge}>Starting soon</span>
                    )}
                    <strong style={appointmentName}>
                      {appointment.patientFirstName} {appointment.patientLastName}
                    </strong>
                    <span style={appointmentTime}>
                      {appointment.workerScheduledForText ||
                        formatAppointmentDate(appointment.scheduledForText)}
                    </span>
                    {appointment.caregiverScheduledEmailSentAt && (
                      <span style={emailDeliveryBadge}>Email notification delivered</span>
                    )}
                    {appointment.caregiverEmailReminderSentAt && (
                      <span style={emailDeliveryBadge}>Your reminder email sent</span>
                    )}
                    {appointment.elderEmailReminderSentAt && (
                      <span style={emailDeliveryBadge}>Elder reminder email sent</span>
                    )}
                    {appointment.notes && <p style={appointmentNotes}>{appointment.notes}</p>}
                    {appointment.patientWhatsapp && (
                      <a
                        href={`https://wa.me/${String(appointment.patientWhatsapp || "").replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        style={secondaryLink}
                      >
                        Open WhatsApp
                      </a>
                    )}
                    <button
                      type="button"
                      style={cancelButton}
                      onClick={() => cancelAppointment(appointment.appointmentID)}
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={emptyText}>No WhatsApp calls scheduled yet.</p>
            )}
          </div>
        </div>

        <div ref={mapSectionRef} style={mapFrame} className="activated-map-frame">
          <MapContainer
            center={[20, 0]}
            zoom={2}
            minZoom={2}
            scrollWheelZoom
            preferCanvas
            zoomAnimation={false}
            fadeAnimation={false}
            markerZoomAnimation={false}
            worldCopyJump
            style={mapStyle}
            className="activated-map-canvas"
          >
            <MapViewportUpdater points={mapPoints} />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              updateWhenIdle
              updateWhenZooming={false}
              keepBuffer={1}
              detectRetina={false}
            />

            {patients.map((p) =>
              p.latitude && p.longitude ? (
                <Marker
                  key={p.patientID}
                  position={[p.latitude, p.longitude]}
                  icon={blackIcon}
                >
                  <Popup maxWidth={300}>
                    <strong>
                      {p.firstName} {p.lastName}
                    </strong>

                    <hr />

                    <p>
                      <b>{t("map:popup.gender")}:</b>{" "}
                      {p.gender === "M" ? t("profile:fields.male") : t("profile:fields.female")}
                    </p>
                    <p>
                      <b>{t("map:popup.language")}:</b> {p.spokenLanguage}
                    </p>
                    <p>
                      <b>{t("map:popup.location")}:</b> {p.city}, {p.country}
                    </p>
                    <p>
                      <b>{t("map:popup.phone")}:</b> {p.whatsapp}
                    </p>

                    <a
                      href={`https://wa.me/${p.whatsapp.replace("+", "")}`}
                      target="_blank"
                      rel="noreferrer"
                      style={whatsAppBtn}
                    >
                      {t("map:popup.contactWhatsapp")}
                    </a>

                  </Popup>
                </Marker>
              ) : null
            )}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default ActivatedMap;

const formatAppointmentDate = (value) => {
  if (!value) return "Time not set";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const MapViewportUpdater = ({ points }) => {
  const map = useMap();
  const lastBoundsSignatureRef = useRef("");

  useEffect(() => {
    if (!points.length) return;

    const nextSignature = points
      .map(([latitude, longitude]) => `${Number(latitude).toFixed(5)}:${Number(longitude).toFixed(5)}`)
      .sort()
      .join("|");

    if (lastBoundsSignatureRef.current === nextSignature) {
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {
      padding: [42, 42],
      maxZoom: 6
    });
    lastBoundsSignatureRef.current = nextSignature;
  }, [map, points]);

  return null;
};

/* =====================
   STYLES
===================== */

const responsiveStyles = `
  .activated-mobile-nav {
    display: none;
  }

  .map-side-panel {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.42) transparent;
  }

  .activated-map-canvas {
    background: #dbeafe;
  }

  .care-map-marker-shell {
    background: transparent;
    border: none;
  }

  .care-map-marker {
    position: relative;
    width: 46px;
    height: 46px;
    border-radius: 16px 16px 16px 4px;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #ffffff;
    box-shadow: 0 14px 26px rgba(15, 23, 42, 0.24);
  }

  .care-map-marker::after {
    content: "";
    position: absolute;
    inset: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
  }

  .care-map-marker span {
    position: relative;
    z-index: 1;
    transform: rotate(45deg);
    color: #ffffff;
    font-size: 0.88rem;
    font-weight: 900;
    line-height: 1;
  }

  .care-map-marker-elder {
    background: linear-gradient(135deg, #111827, #475569);
  }

  @media (max-width: 980px) {
    .activated-mobile-nav {
      position: sticky;
      top: 8px;
      z-index: 900;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      max-width: 560px;
      margin: 0 auto 12px;
      padding: 7px;
      border: 1px solid rgba(255, 255, 255, 0.74);
      border-radius: 18px;
      background: rgba(14, 85, 128, 0.92);
      box-shadow: 0 12px 28px rgba(15, 42, 67, 0.2);
      backdrop-filter: blur(14px);
    }

    .activated-mobile-nav button {
      min-height: 44px;
      border: 0;
      border-radius: 13px;
      background: rgba(255, 255, 255, 0.14);
      color: #ffffff;
      font-weight: 800;
    }

    .activated-mobile-nav button:last-child {
      background: #f1c40f;
      color: #17324a;
    }

    .map-page-layout {
      grid-template-columns: 1fr !important;
    }

    .map-side-panel {
      max-height: none !important;
      position: static !important;
      scroll-margin-top: 78px;
    }

    .activated-map-frame {
      position: static !important;
      height: auto !important;
      min-height: 0 !important;
      scroll-margin-top: 78px;
    }

    .activated-map-canvas {
      height: 64svh !important;
      min-height: 380px !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  padding: "0 18px 24px",
  background: "linear-gradient(180deg, #edf4f8 0%, #dfe9f2 100%)"
};

const layout = {
  display: "grid",
  gridTemplateColumns: "minmax(280px, 320px) minmax(0, 1fr)",
  gap: "18px",
  alignItems: "start",
  minHeight: 0,
  maxWidth: "1400px",
  margin: "0 auto"
};

const legendPanel = {
  background: "linear-gradient(180deg, #0e5580 0%, #156893 100%)",
  padding: "24px",
  borderRadius: "28px",
  border: "1px solid rgba(255, 208, 63, 0.16)",
  boxShadow: "0 24px 48px rgba(12, 42, 68, 0.14)",
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  maxHeight: "calc(100vh - 140px)",
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable",
  position: "sticky",
  top: "122px"
};

const legendTitle = {
  fontSize: "21px",
  fontWeight: "700",
  color: "#ffffff",
  marginBottom: "5px"
};

const legendCard = {
  background: "rgba(255, 255, 255, 0.1)",
  padding: "18px",
  borderRadius: "18px",
  boxShadow: "0 12px 28px rgba(0,0,0,0.06)",
  borderLeft: "5px solid #ffd24a"
};

const backToProfileButton = {
  width: "100%",
  minHeight: "44px",
  borderRadius: "14px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  background: "rgba(255, 255, 255, 0.12)",
  color: "#ffffff",
  fontWeight: "900",
  cursor: "pointer",
  textAlign: "left",
  padding: "0 14px"
};

const availabilityCard = {
  background: "#ffffff",
  padding: "16px",
  borderRadius: "18px",
  display: "grid",
  gap: "12px",
  border: "1px solid rgba(255,255,255,0.22)",
  boxShadow: "0 12px 28px rgba(0,0,0,0.08)"
};

const availabilityTitle = {
  display: "block",
  marginTop: "4px",
  color: "#102a43",
  fontSize: "18px"
};

const availabilityCopy = {
  margin: "5px 0 0",
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.45
};

const availabilityButton = {
  minHeight: "44px",
  border: "none",
  borderRadius: "12px",
  color: "#ffffff",
  fontWeight: "900",
  cursor: "pointer"
};

const goUnavailableButton = {
  ...availabilityButton,
  background: "linear-gradient(135deg, #be123c, #e11d48)"
};

const goAvailableButton = {
  ...availabilityButton,
  background: "linear-gradient(135deg, #15803d, #16a34a)"
};

const scheduleCard = {
  background: "#ffffff",
  padding: "18px",
  borderRadius: "18px",
  boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
  display: "grid",
  gap: "10px"
};

const sectionEyebrow = {
  color: "#0e5580",
  fontSize: "11px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const scheduleTitle = {
  margin: 0,
  color: "#102a43",
  fontSize: "17px",
  fontWeight: "800"
};

const scheduleCopy = {
  margin: 0,
  color: "#486581",
  lineHeight: 1.5,
  fontSize: "13px"
};

const secondaryLink = {
  minHeight: "40px",
  borderRadius: "12px",
  border: "1px solid rgba(14, 85, 128, 0.18)",
  color: "#0e5580",
  textDecoration: "none",
  fontWeight: "800",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "0 12px"
};

const messageBox = {
  borderRadius: "10px",
  background: "#fff7ed",
  color: "#9a3412",
  padding: "10px 12px",
  fontSize: "13px",
  lineHeight: 1.45,
  border: "1px solid #fed7aa",
  fontWeight: "800"
};

const alertHealthCard = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  padding: "10px",
  borderRadius: "12px",
  background: "#f7fafc",
  border: "1px solid #e2e8f0"
};

const alertHealthDot = {
  width: "10px",
  height: "10px",
  marginTop: "4px",
  borderRadius: "50%",
  flexShrink: 0,
  boxShadow: "0 0 0 4px rgba(15, 95, 88, 0.09)"
};

const alertHealthCopy = {
  display: "grid",
  gap: "3px",
  color: "#334e68",
  fontSize: "11px",
  lineHeight: 1.4
};

const appointmentsCard = {
  background: "rgba(255, 255, 255, 0.12)",
  border: "1px solid rgba(255,255,255,0.16)",
  padding: "18px",
  borderRadius: "18px",
  display: "grid",
  gap: "12px"
};

const appointmentList = {
  display: "grid",
  gap: "10px"
};

const appointmentItem = {
  padding: "12px",
  borderRadius: "14px",
  background: "#ffffff",
  color: "#102a43",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  display: "grid",
  gap: "6px"
};

const urgentAppointmentItem = {
  border: "2px solid #f97316",
  background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
  boxShadow: "0 16px 32px rgba(249, 115, 22, 0.2)"
};

const soonAppointmentItem = {
  border: "1px solid #facc15",
  background: "linear-gradient(180deg, #fefce8 0%, #ffffff 100%)"
};

const urgentBadge = {
  justifySelf: "start",
  borderRadius: "999px",
  background: "#f97316",
  color: "#ffffff",
  padding: "5px 9px",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const soonBadge = {
  ...urgentBadge,
  background: "#ca8a04"
};

const appointmentName = {
  display: "block",
  fontSize: "14px"
};

const appointmentTime = {
  display: "block",
  marginTop: "4px",
  color: "#0e5580",
  fontSize: "13px",
  fontWeight: "800"
};

const emailDeliveryBadge = {
  display: "inline-flex",
  marginTop: "8px",
  padding: "5px 8px",
  borderRadius: "999px",
  background: "#dcfce7",
  color: "#166534",
  fontSize: "11px",
  fontWeight: "900"
};

const appointmentNotes = {
  margin: "8px 0 0",
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.45
};

const emptyText = {
  color: "rgba(255,255,255,0.82)",
  fontSize: "13px",
  lineHeight: 1.5,
  margin: 0
};

const cancelButton = {
  marginTop: "10px",
  minHeight: "34px",
  border: "none",
  borderRadius: "10px",
  background: "#fee2e2",
  color: "#991b1b",
  cursor: "pointer",
  fontWeight: "800",
  padding: "0 12px"
};

const legendItem = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "12px",
  fontWeight: "600",
  fontSize: "17px",
  color: "#ffffff"
};

const legendDot = {
  width: "30px",
  height: "30px",
  borderRadius: "10px",
  background: "#111827",
  color: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "900",
  border: "2px solid rgba(255,255,255,0.85)",
  boxShadow: "0 8px 18px rgba(0,0,0,0.16)",
  flex: "0 0 auto"
};

const legendText = {
  fontSize: "17px"
};

const legendDescription = {
  fontSize: "15px",
  color: "rgba(255, 255, 255, 0.88)",
  lineHeight: "1.7",
  margin: "0 0 16px"
};

const highlight = {
  color: "#ffe07d",
  fontWeight: "700"
};

const errorBox = {
  padding: "10px 12px",
  borderRadius: "12px",
  background: "#fff1f0",
  color: "#c0392b",
  fontSize: "14px",
  border: "1px solid #f2c6c2"
};

const mapFrame = {
  position: "sticky",
  top: "122px",
  height: "calc(100svh - 150px)",
  minHeight: "560px",
  boxSizing: "border-box",
  background: "rgba(255, 255, 255, 0.78)",
  padding: "14px",
  borderRadius: "20px",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  boxShadow: "0 26px 56px rgba(17, 44, 77, 0.14)"
};

const mapStyle = {
  height: "100%",
  minHeight: 0,
  width: "100%",
  borderRadius: "14px"
};

const whatsAppBtn = {
  display: "block",
  marginTop: "10px",
  padding: "10px 12px",
  background: "#0e5580",
  color: "#fff",
  textAlign: "center",
  textDecoration: "none",
  borderRadius: "10px",
  fontWeight: "700"
};
